import React, { useEffect, useRef, useState } from 'react';
import { db } from '../../servicios/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { FaQrcode, FaUser, FaRoute, FaClock, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

export default function ValidarBoleto() {
  const [ticketId, setTicketId] = useState('');
  const [ticket, setTicket] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    if (resultado) return;
    const scanner = new Html5QrcodeScanner('reader', { fps: 10, qrbox: 250 }, false);
    scannerRef.current = scanner;
    scanner.render(async (decodedText) => {
      if (loading) return;
      try { await scanner.clear(); } catch {}
      buscarBoleto(decodedText.trim());
    });
    return () => { try { scanner.clear(); } catch {} };
  }, [resultado, loading]);

  // Busca el nombre del pasajero en los distintos campos que usan online vs taquilla
  const getNombre = (asiento, data) =>
    data.pasajeros?.[asiento] ||
    data.ocupados?.find(o => (typeof o === 'object' ? o.asiento : o) === asiento)?.nombre ||
    data.contacto?.nombre ||
    'Sin nombre';

  const buscarBoleto = async (id) => {
    if (!id) return;
    setLoading(true);
    setTicket(null);
    setResultado(null);

    try {
      const ref = doc(db, 'tickets', id);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        setResultado({ tipo: 'error' });
        return;
      }

      const data = snap.data();

      /*
        NORMALIZAR PASAJEROS
        ─────────────────────────────────────────
        ONLINE:   asientosIda[], asientosRegreso[], pasajeros{}
        TAQUILLA: asientos[], ocupados[{asiento, nombre}]
                  (redondo taquilla también tiene asientosRegreso[])
      */
      let pasajeros = [];

      if (data.tipoViaje === 'redondo') {
        // Online usa asientosIda[], taquilla usa asientos[]
        const asientosIdaArr = data.asientosIda?.length > 0
          ? data.asientosIda
          : (data.asientos || []);

        const asientosRegresoArr = data.asientosRegreso || [];

        pasajeros = asientosIdaArr.map((asientoIda, i) => ({
          nombre: getNombre(asientoIda, data),
          asientoIda: asientoIda.replace('a', ''),
          asientoRegreso: asientosRegresoArr[i]
            ? asientosRegresoArr[i].replace('a', '')
            : '—',
        }));

        // Último recurso: reconstruir desde ocupados[]
        if (pasajeros.length === 0 && data.ocupados?.length > 0) {
          pasajeros = data.ocupados.map((o, i) => ({
            nombre: typeof o === 'object'
              ? (o.nombre || data.contacto?.nombre || 'Sin nombre')
              : (data.contacto?.nombre || 'Sin nombre'),
            asientoIda: (typeof o === 'object' ? o.asiento : o).replace('a', ''),
            asientoRegreso: asientosRegresoArr[i]
              ? asientosRegresoArr[i].replace('a', '')
              : '—',
          }));
        }

      } else {
        const asientosArr = data.asientos?.length > 0 ? data.asientos : null;

        if (asientosArr) {
          pasajeros = asientosArr.map(asiento => ({
            nombre: getNombre(asiento, data),
            asiento: asiento.replace('a', ''),
          }));
        } else if (data.ocupados?.length > 0) {
          pasajeros = data.ocupados.map(o => ({
            nombre: typeof o === 'object'
              ? (o.nombre || data.contacto?.nombre || 'Sin nombre')
              : (data.contacto?.nombre || 'Sin nombre'),
            asiento: (typeof o === 'object' ? o.asiento : o).replace('a', ''),
          }));
        }
      }

      setTicket({ id, ...data, pasajerosProcesados: pasajeros });

      if (data.tipoViaje === 'redondo') {
        setResultado({ tipo: 'redondo' });
      } else {
        if (data.estado === 'usado') {
          setResultado({ tipo: 'usado' });
        } else if (data.estado === 'cancelado' || data.estado === 'cancelado_sin_reembolso') {
          setResultado({ tipo: 'cancelado' });
        } else {
          await updateDoc(ref, {
            estado: 'usado',
            validado: true,
            validadoEn: new Date().toLocaleString('es-MX'),
          });
          setResultado({ tipo: 'valido' });
        }
      }

    } catch (e) {
      console.error('Error buscando boleto:', e);
      setResultado({ tipo: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validarRedondo = async (trayecto) => {
    if (!ticket) return;
    const ref = doc(db, 'tickets', ticket.id);
    const ahora = new Date().toLocaleString('es-MX');
    try {
      if (trayecto === 'ida' && !ticket.validadoIda) {
        await updateDoc(ref, { validadoIda: true, validadoEnIda: ahora });
        setTicket(prev => ({ ...prev, validadoIda: true, validadoEnIda: ahora }));
      }
      if (trayecto === 'regreso' && !ticket.validadoRegreso) {
        await updateDoc(ref, { validadoRegreso: true, validadoEnRegreso: ahora });
        setTicket(prev => ({ ...prev, validadoRegreso: true, validadoEnRegreso: ahora }));
      }
    } catch (error) {
      console.error('Error validando trayecto:', error);
    }
  };

  const reiniciar = () => {
    setResultado(null);
    setTicket(null);
    setTicketId('');
  };

  // helpers rutas — soporta taquilla y online
  const getOrigenIda  = (t) => t?.rutaIda?.origen  || t?.ruta?.origen  || '—';
  const getDestinoIda = (t) => t?.rutaIda?.destino || t?.ruta?.destino || '—';
  const getFechaIda   = (t) => t?.fechaIda  || t?.fechaViaje || '—';
  const getHoraIda    = (t) => t?.horaIda   || t?.horaViaje  || '—';

  return (
    <div className="min-h-screen bg-slate-900 p-4 flex flex-col items-center">
      <h1 className="text-white text-xl md:text-2xl font-black mb-4 flex items-center gap-2">
        <FaQrcode /> Scanner de Abordaje
      </h1>

      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">

        {/* SCANNER */}
        {!resultado && (
          <div className="flex flex-col">
            <div id="reader" className="w-full" />
            <div className="p-4 border-t">
              <form
                onSubmit={(e) => { e.preventDefault(); buscarBoleto(ticketId.trim().toUpperCase()); }}
                className="flex gap-2"
              >
                <input
                  value={ticketId}
                  onChange={e => setTicketId(e.target.value.toUpperCase())}
                  className="flex-1 p-3 bg-gray-100 rounded-xl text-center font-bold text-sm tracking-widest"
                  placeholder="ID BOLETO"
                />
                <button type="submit" className="bg-blue-600 text-white px-6 rounded-xl font-bold hover:bg-blue-700 transition">
                  OK
                </button>
              </form>
            </div>
          </div>
        )}

        {/* RESULTADO */}
        {resultado && (
          <div className="p-4 max-h-[80vh] overflow-y-auto space-y-4">

            {/* BANNER */}
            <div className={`p-4 rounded-xl text-center font-black text-white text-lg ${
              resultado.tipo === 'valido'    ? 'bg-green-600' :
              resultado.tipo === 'usado'     ? 'bg-orange-600' :
              resultado.tipo === 'cancelado' ? 'bg-gray-600' :
              resultado.tipo === 'error'     ? 'bg-red-600' :
                                               'bg-blue-600'
            }`}>
              {resultado.tipo === 'valido' && (
                <div className="flex items-center justify-center gap-2">
                  <FaCheckCircle className="text-2xl" /> ✅ BOLETO VÁLIDO — PUEDE ABORDAR
                </div>
              )}
              {resultado.tipo === 'usado' && (
                <div className="flex items-center justify-center gap-2">
                  <FaExclamationTriangle className="text-2xl" /> ⚠️ BOLETO YA UTILIZADO
                </div>
              )}
              {resultado.tipo === 'cancelado' && '🚫 BOLETO CANCELADO'}
              {resultado.tipo === 'error'     && '❌ BOLETO NO ENCONTRADO'}
              {resultado.tipo === 'redondo'   && '🎫 BOLETO REDONDO — SELECCIONA TRAYECTO'}
            </div>

            {ticket && (
              <>
                {/* TIPO + CANAL */}
                <div className="bg-slate-100 p-3 rounded-xl flex justify-between items-center">
                  <div>
                    <p className="text-xs text-gray-500 font-bold uppercase">Tipo de Boleto</p>
                    <p className="text-base font-black text-slate-800">
                      {ticket.tipoViaje === 'redondo' ? '⮂ Viaje Redondo' : '→ Viaje Sencillo'}
                    </p>
                  </div>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                    ticket.metodoPago === 'efectivo_taquilla'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {ticket.metodoPago === 'efectivo_taquilla' ? '🏢 Taquilla' : '🌐 En línea'}
                  </span>
                </div>

                {/* RUTA SENCILLO */}
                {ticket.tipoViaje !== 'redondo' && (
                  <div className="bg-slate-900 text-white p-4 rounded-xl">
                    <p className="text-xs opacity-60 flex items-center gap-1 mb-3 font-bold uppercase">
                      <FaRoute /> Ruta
                    </p>
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <p className="text-xs opacity-60">Origen</p>
                        <p className="text-base font-bold">{getOrigenIda(ticket)}</p>
                      </div>
                      <div className="text-2xl opacity-40">→</div>
                      <div className="text-right">
                        <p className="text-xs opacity-60">Destino</p>
                        <p className="text-base font-bold">{getDestinoIda(ticket)}</p>
                      </div>
                    </div>
                    <p className="text-sm opacity-70 flex items-center gap-1">
                      <FaClock /> {getFechaIda(ticket)} • {getHoraIda(ticket)}
                    </p>
                    {ticket.validado && ticket.validadoEn && (
                      <div className="mt-3 bg-orange-500 p-2 rounded text-xs font-bold text-center">
                        ✓ Validado: {ticket.validadoEn}
                      </div>
                    )}
                  </div>
                )}

                {/* RUTA REDONDO */}
                {ticket.tipoViaje === 'redondo' && (
                  <>
                    <div className="bg-blue-50 p-4 rounded-xl border-l-4 border-blue-600">
                      <p className="text-xs text-blue-700 font-black uppercase mb-2 flex items-center gap-1">
                        <FaRoute /> Viaje de Ida
                      </p>
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <p className="text-xs text-gray-500">Origen</p>
                          <p className="font-bold text-slate-900">{getOrigenIda(ticket)}</p>
                        </div>
                        <div className="text-blue-400 text-xl">→</div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Destino</p>
                          <p className="font-bold text-slate-900">{getDestinoIda(ticket)}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <FaClock className="text-blue-600" />
                        {getFechaIda(ticket)} • {getHoraIda(ticket)}
                      </p>
                      {ticket.validadoIda && (
                        <div className="mt-2 bg-orange-100 text-orange-700 p-2 rounded text-xs font-bold text-center">
                          ✓ Validado ida: {ticket.validadoEnIda}
                        </div>
                      )}
                    </div>

                    <div className="bg-purple-50 p-4 rounded-xl border-l-4 border-purple-600">
                      <p className="text-xs text-purple-700 font-black uppercase mb-2 flex items-center gap-1">
                        <FaRoute /> Viaje de Regreso
                      </p>
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <p className="text-xs text-gray-500">Origen</p>
                          <p className="font-bold text-slate-900">
                            {ticket.rutaRegreso?.origen || getDestinoIda(ticket)}
                          </p>
                        </div>
                        <div className="text-purple-400 text-xl">→</div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Destino</p>
                          <p className="font-bold text-slate-900">
                            {ticket.rutaRegreso?.destino || getOrigenIda(ticket)}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <FaClock className="text-purple-600" />
                        {ticket.fechaRegreso || '—'} • {ticket.horaRegreso || '—'}
                      </p>
                      {ticket.validadoRegreso && (
                        <div className="mt-2 bg-orange-100 text-orange-700 p-2 rounded text-xs font-bold text-center">
                          ✓ Validado regreso: {ticket.validadoEnRegreso}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* PASAJEROS */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 font-bold mb-3 flex items-center gap-1 uppercase">
                    <FaUser /> Pasajeros ({ticket.pasajerosProcesados?.length || 0})
                  </p>
                  <div className="space-y-2">
                    {ticket.pasajerosProcesados?.length > 0 ? (
                      ticket.pasajerosProcesados.map((p, i) => (
                        <div key={i} className="flex justify-between items-center bg-white p-3 rounded-xl shadow-sm">
                          <span className="font-bold text-slate-800 flex-1 text-sm">{p.nombre}</span>
                          <div className="flex gap-1 flex-shrink-0">
                            {ticket.tipoViaje === 'redondo' ? (
                              <>
                                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded font-bold text-xs">
                                  Ida: {p.asientoIda}
                                </span>
                                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded font-bold text-xs">
                                  Reg: {p.asientoRegreso}
                                </span>
                              </>
                            ) : (
                              <span className="bg-slate-900 text-white px-3 py-1 rounded-full font-black text-xs">
                                #{p.asiento}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-gray-400 text-center py-2">Sin datos de pasajeros</p>
                    )}
                  </div>
                </div>

                {/* BOTONES REDONDO */}
                {ticket.tipoViaje === 'redondo' && (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => validarRedondo('ida')}
                      disabled={ticket.validadoIda}
                      className={`py-4 rounded-xl font-black text-white transition ${
                        ticket.validadoIda
                          ? 'bg-orange-500 cursor-not-allowed opacity-80'
                          : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
                      }`}
                    >
                      {ticket.validadoIda ? '✓ IDA USADA' : 'VALIDAR IDA'}
                    </button>
                    <button
                      onClick={() => validarRedondo('regreso')}
                      disabled={ticket.validadoRegreso}
                      className={`py-4 rounded-xl font-black text-white transition ${
                        ticket.validadoRegreso
                          ? 'bg-orange-500 cursor-not-allowed opacity-80'
                          : 'bg-purple-600 hover:bg-purple-700 active:scale-95'
                      }`}
                    >
                      {ticket.validadoRegreso ? '✓ REGRESO USADO' : 'VALIDAR REGRESO'}
                    </button>
                  </div>
                )}
              </>
            )}

            <button
              onClick={reiniciar}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white py-4 rounded-xl font-black transition active:scale-95"
            >
              ESCANEAR SIGUIENTE BOLETO
            </button>
          </div>
        )}
      </div>
    </div>
  );
}